import { type Requirement, Status } from "../../redux/interfaces/folder.types";

type IdentityRequirementState = Pick<
  Requirement,
  "status" | "workflow_step" | "verification_data"
>;

const TERMINAL_WORKFLOW_STEPS = new Set([
  "completed",
  "pending_review",
  "data_confirmation",
  "data_edit",
]);

const BLOCKING_WORKFLOW_STEPS = new Set([
  "upload",
  "validating",
  "address_check",
  "rejected",
]);

function isNonEmptyRecord(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  );
}

/**
 * Derive identity completion from persisted backend state.
 *
 * `pending_review` is overloaded: it can mean a terminal/manual-review result,
 * but it is also written temporarily while an upload batch is bound. The
 * workflow step (or stored evidence for legacy rows) disambiguates both cases.
 */
export function isIdentityRequirementComplete(
  requirement: IdentityRequirementState | null | undefined,
): boolean {
  if (!requirement) return true;

  const workflowStep = requirement.workflow_step ?? null;
  if (workflowStep && BLOCKING_WORKFLOW_STEPS.has(workflowStep)) return false;

  if (requirement.status === Status.Valid) return true;
  if (requirement.status !== Status.PendingReview) return false;

  if (
    isNonEmptyRecord(requirement.verification_data) &&
    isNonEmptyRecord(requirement.verification_data._identity_verification_error)
  ) {
    return false;
  }

  if (workflowStep && TERMINAL_WORKFLOW_STEPS.has(workflowStep)) return true;

  // Match develop for old/manual-review records that predate workflow_step:
  // pending_review is terminal even when the provider stored no OCR payload.
  return workflowStep === null;
}
